/**
 * MyAlertsPanel — synthesised personal notifications for the member Projects
 * dashboard. No new backend; everything derives from data already in flight:
 *   · overdue tasks assigned to / created by me
 *   · tasks due within the next 3 days
 *   · milestones on my projects due within 7 days (or already overdue)
 *   · my projects that are At Risk / Critical / overdue
 *
 * Reuses the themed `<AlertList>` exactly the way HR's `MyAlertsPanel` does and
 * collapses (returns null) when there's nothing worth surfacing.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import AlertList from '@/components/advanced/Widgets/alerts/AlertList';
import type { Alert } from '@/components/advanced/Widgets/types';
import {
  useProjectService,
  useProjectMilestoneService,
  useTaskPortalTaskService,
  type ProjectModelView,
  type ProjectMilestone,
  type TaskModelView,
} from '@actschurch/ccms-sdk';
import { computeHealthStatus, isProjectOverdue, parseBackendDate } from '../../utils/projectHelpers';
import { MEMBER_DASHBOARD_KEY, OPEN_TASK_STATUS_IDS, MY_TASKS_FILTER } from './dashboardConstants';

export function MyAlertsPanel() {
  const projectService = useProjectService();
  const milestoneService = useProjectMilestoneService();
  const taskService = useTaskPortalTaskService();

  const { data: myProjects = [] } = useQuery({
    queryKey: [...MEMBER_DASHBOARD_KEY, 'my-projects'],
    queryFn: () => projectService.getMyListView(),
  });
  const { data: myTasks = [] } = useQuery({
    queryKey: [...MEMBER_DASHBOARD_KEY, 'my-tasks'],
    queryFn: () => taskService.getMyTasks(MY_TASKS_FILTER),
  });
  const { data: milestones = [] } = useQuery({
    queryKey: [...MEMBER_DASHBOARD_KEY, 'milestones'],
    queryFn: () => milestoneService.getAll(),
  });

  const alerts = useMemo<Alert[]>(() => {
    const out: Alert[] = [];
    const today = dayjs().startOf('day');

    const openTasks = (myTasks as TaskModelView[]).filter((t) =>
      OPEN_TASK_STATUS_IDS.has(t.statusID)
    );

    // 1) Overdue tasks
    const overdueTasks = openTasks.filter((t) => {
      if (t.isOverDue ?? t.isOverdue) return true;
      const due = parseBackendDate(t.dueDate ?? t.endDate);
      return !!due && dayjs(due).isBefore(today);
    });
    if (overdueTasks.length > 0) {
      out.push({
        id: 'tasks-overdue',
        title: 'Overdue tasks',
        message: `You have ${overdueTasks.length} overdue ${overdueTasks.length === 1 ? 'task' : 'tasks'} that need attention.`,
        severity: 'error',
        category: 'user',
        timestamp: new Date(),
      });
    }

    // 2) Tasks due within the next 3 days (not already overdue)
    const soonCutoff = today.add(3, 'day');
    const dueSoonTasks = openTasks.filter((t) => {
      const due = parseBackendDate(t.dueDate ?? t.endDate);
      if (!due) return false;
      const d = dayjs(due);
      return (d.isAfter(today) || d.isSame(today, 'day')) && d.isBefore(soonCutoff);
    });
    if (dueSoonTasks.length > 0) {
      out.push({
        id: 'tasks-due-soon',
        title: 'Tasks due soon',
        message: `${dueSoonTasks.length} ${dueSoonTasks.length === 1 ? 'task is' : 'tasks are'} due within the next 3 days.`,
        severity: 'warning',
        category: 'user',
        timestamp: new Date(),
      });
    }

    // 3) Milestones on my projects due within 7 days or already overdue
    const active = (myProjects as ProjectModelView[]).filter((p) => !p.isArchived);
    const myProjectIds = new Set(active.map((p) => p.projectID));
    const milestoneCutoff = today.add(7, 'day');
    let overdueMilestones = 0;
    let dueSoonMilestones = 0;
    (milestones as ProjectMilestone[]).forEach((m) => {
      if (!myProjectIds.has(m.projectID)) return;
      const due = parseBackendDate(m.dueDate);
      if (!due) return;
      const d = dayjs(due);
      if (d.isBefore(today)) overdueMilestones++;
      else if (d.isBefore(milestoneCutoff)) dueSoonMilestones++;
    });
    if (overdueMilestones > 0) {
      out.push({
        id: 'milestones-overdue',
        title: 'Milestones overdue',
        message: `${overdueMilestones} milestone${overdueMilestones === 1 ? '' : 's'} on your projects ${overdueMilestones === 1 ? 'is' : 'are'} past due.`,
        severity: 'error',
        category: 'business',
        timestamp: new Date(),
      });
    }
    if (dueSoonMilestones > 0) {
      out.push({
        id: 'milestones-due-soon',
        title: 'Milestones approaching',
        message: `${dueSoonMilestones} milestone${dueSoonMilestones === 1 ? '' : 's'} due within the next 7 days.`,
        severity: 'warning',
        category: 'business',
        timestamp: new Date(),
      });
    }

    // 4) At-risk / overdue projects I'm on
    const riskyProjects = active.filter((p) => {
      const h = computeHealthStatus(p);
      return h === 'At Risk' || h === 'Critical' || isProjectOverdue(p);
    });
    if (riskyProjects.length > 0) {
      out.push({
        id: 'projects-at-risk',
        title: 'Projects need attention',
        message: `${riskyProjects.length} of your projects ${riskyProjects.length === 1 ? 'is' : 'are'} at risk or overdue.`,
        severity: 'warning',
        category: 'business',
        timestamp: new Date(),
      });
    }

    return out;
  }, [myProjects, myTasks, milestones]);

  if (alerts.length === 0) return null;

  return <AlertList alerts={alerts} maxItems={5} variant="default" />;
}
